// Who may use the « AI for this conversation » switch of a WhatsApp
// conversation. A display gate only, not a permission: the backend lets any
// admin of the AI assistants section give a conversation back to the AI, and
// so does the ticket modal's switch, with no lock (review, 4 Nov 2026).
import { WhatsAppConversation } from "../aiConversations.types";
import { WhatsAppConversationStatus } from "../constants/enums";

export interface ModeSwitchState {
  // In human mode and claimed by another admin.
  ownedByOtherAdmin: boolean;
  // The switch is greyed out.
  switchLocked: boolean;
  // Giving it back to the AI asks first.
  confirmBeforeAi: boolean;
}

// A conversation another admin holds stays locked for everyone else, except a
// Super Admin, who may give it back to the AI after a confirmation (the
// owner's « go », 4 Nov 2026): on dev the messages of a conversation another
// admin held went unanswered for a day, and the owner, a Super Admin, could
// not give it back to the AI. Only giving it back: the composer, and taking
// it over for oneself, stay as they were.
export const modeSwitchState = (
  conversation:
    | Pick<WhatsAppConversation, "status" | "assignedAdminId">
    | undefined,
  currentAdminId: string | null,
  isSuperAdmin: boolean
): ModeSwitchState => {
  const ownedByOtherAdmin = Boolean(
    conversation &&
    conversation.status === WhatsAppConversationStatus.Human &&
    conversation.assignedAdminId &&
    conversation.assignedAdminId !== currentAdminId
  );

  return {
    ownedByOtherAdmin,
    switchLocked: ownedByOtherAdmin && !isSuperAdmin,
    confirmBeforeAi: ownedByOtherAdmin && isSuperAdmin
  };
};

type ModeFields = Pick<
  WhatsAppConversation,
  "id" | "status" | "aiEnabled" | "assignedAdminId"
>;

// The mode a conversation came back with (setMode's answer, the socket's
// conversation.updated), on a copy the page holds. The list's rows were the
// only copy updated, and a deep-linked thread outside the loaded page is read
// from its own query (useGetConversation): given back to the AI, its switch
// would have stayed OFF and its composer locked until a reload (found in
// review, 4 Nov 2026; not observed).
export const withModeOf = <T extends ModeFields>(
  conversation: T,
  updated: ModeFields
): T =>
  conversation.id === updated.id
    ? {
        ...conversation,
        status: updated.status,
        aiEnabled: updated.aiEnabled,
        assignedAdminId: updated.assignedAdminId
      }
    : conversation;
